<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white">
          {{ title }}
        </v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <v-tooltip location="start">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon variant="text" @click="cancel">
                <v-icon>mdi-close-circle-outline</v-icon>
              </v-btn>
            </template>
            <span>Fermer</span>
          </v-tooltip>

          <v-tooltip location="start">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon variant="text" @click="save">
                <v-icon>mdi-content-save-all</v-icon>
              </v-btn>
            </template>
            <span>Sauvegarder</span>
          </v-tooltip>

          <v-tooltip location="start" v-if="canRemove">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon variant="text" @click="remove">
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
            <span>Supprimer</span>
          </v-tooltip>
        </div>
      </v-toolbar>

      <v-card-text>
        <v-container fluid>
          <v-row density="compact">
            <v-col cols="12" md="6">
             <v-text-field v-model="panne.productAquaNumber" label="Numéro Aqua" readonly>
             </v-text-field>
           </v-col>
           <v-col cols="12" md="6">
             <v-text-field v-model="panne.creationDate" type="date" hint="La date de la panne" label="Date de création" readonly>
             </v-text-field>
           </v-col>
         </v-row>

          <v-row density="compact">
            <v-col cols="12" md="12">
              <v-text-field v-model="panne.name" hint="Le sujet de la panne">
                <template #label> Sujet <span class="text-red">*</span> </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row density="compact">
            <v-col cols="12">
              <v-textarea v-model="panne.desc" hint="Observation à propos de la panne">
                <template #label> Observation <span class="text-red">*</span> </template>
              </v-textarea>
            </v-col>
          </v-row>

          <v-row density="compact" v-if="!isCreatingNewPanne()">
            <v-col cols="12" md="6">
              <v-checkbox v-model="panne.repaired" label="Panne réparée" readonly hide-details>
              </v-checkbox>
            </v-col>
            <v-col cols="12" md="6" v-if="panne.repaired">
              <v-text-field v-model="panne.reparationDate" type="date" label="Date de réparation">
              </v-text-field>
            </v-col>
          </v-row>

          <v-row density="compact" v-if="!isCreatingNewPanne()">
            <v-col cols="12">
              <v-card variant="outlined">
                <v-card-title>Activité liée</v-card-title>
                <v-divider />
                <v-card-text>
                  <div v-if="!taskByPanne" class="text-medium-emphasis">Aucune activité liée</div>
                  <div v-else class="d-flex flex-column ga-2">
                    <div class="d-flex flex-column ga-1">
                      <div><strong>Sujet:</strong> {{ taskByPanne.subject || "-" }}</div>
                      <div><strong>Type:</strong> {{ taskByPanne.taskType || "-" }}</div>
                      <div><strong>Status:</strong> {{ taskByPanne.taskStatus || "-" }}</div>
                      <div><strong>Priorité:</strong> {{ taskByPanne.priority || "-" }}</div>
                      <div v-if="taskByPanne.observation"><strong>Observation:</strong> {{ taskByPanne.observation }}</div>
                    </div>
                    <div class="d-flex justify-end">
                      <v-btn color="primary" variant="text" @click="goToTask">
                        Ouvrir l'activité
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="mt-4 mb-6" />

          <!-- INSTALLED SPAREPARTS -->
          <div v-for="(installedSparepart, j) in panne.installedSpareParts || []" :key="j"
            class="mb-2 d-flex ga-3 align-center">
            <div style="width: 75%" :class="{
              'red-outline-wrapper': installedSparepart.sparepartId && !isSparePartAvailable(installedSparepart.sparepartId),
              'green-outline-wrapper': installedSparepart.sparepartId && isSparePartAvailable(installedSparepart.sparepartId)
            }">
              <v-autocomplete :label="getSparePartLabel(installedSparepart.sparepartId)" :items="spareparts"
                v-model="installedSparepart.sparepartId" item-title="displayName" item-value="id"
                :disabled="panne.repaired" hide-details="auto" />
            </div>

            <v-text-field style="width: 25%" type="number" label="Quantité" v-model="installedSparepart.quantity"
              :disabled="panne.repaired" hide-details="auto" />

            <v-btn
              icon
              variant="text"
              color="error"
              :disabled="panne.repaired"
              @click="removeInstalledSparePart(panne, j)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>

          <!-- ADD INSTALLED SPAREPART -->
          <div class="d-flex justify-end mt-2">
            <v-btn size="small" variant="outlined" color="primary" :disabled="panne.repaired"
              @click="addInstalledSparePart(panne)">
              Ajouter pièce détachée
            </v-btn>
          </div>

        </v-container>
      </v-card-text>
    </v-card>

    <ConfirmDialog v-model="removePanneDialog" dialogTitle="Suppression" dialogText="Voulez-vous supprimer cette panne?"
      @confirm="onConfirm" @cancel="onCancel" />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useAppStore } from "@/store/app";
import { Panne, Product, SparePart, Task } from "@/types";
import { deletePanne, getPanneById, postPanne, putPanne } from "@/utils/panne-api";
import { fetchTaskByPanne } from "@/utils/task-api";
import { fetchFilteredSpareParts } from "@/utils/sparepart-api";
import { getProductById } from "@/utils/product-api";

const props = defineProps<{
  productId: string;
}>();

const router = useRouter();
const route = useRoute();
const appStore = useAppStore();

const removePanneDialog = ref(false);
const loading = ref(false);

const title = ref("");
const panne = ref<Panne>({ } as Panne);
const taskByPanne = ref<Task | null>(null);
const product = ref<Product>({} as Product);
const spareparts = ref<SparePart[]>([]);

const canRemove = computed(() => !!panne.value.id);
const hasInstalledSpareParts = computed(
  () => (panne.value.installedSpareParts?.length ?? 0) > 0
);

async function save() {
  loading.value = true;
  try {
    if (!panne.value.id) {
      panne.value = await postPanne(panne.value, props.productId);
      taskByPanne.value = await fetchTaskByPanne(panne.value.id as string);

      router.replace({
        name: "panne",
        params: { productId: props.productId, id: panne.value.id },
      });

      title.value = "Editer la panne";
      appStore.sendSuccessNotice("New panne has been added.");
    } else {
      await putPanne(panne.value);
      appStore.sendSuccessNotice("Panne has been updated.");
    }
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function cancel() {
  router.push({
    name: "product",
    params: { id: props.productId },
  });
}

function goToTask() {
  if (!taskByPanne.value?.id) {
    return;
  }

  router.push({
    name: "task",
    params: { id: taskByPanne.value.id },
  });
}

function remove() {
  removePanneDialog.value = true;
}

function addInstalledSparePart(panne: Panne): void {
  if (!panne.installedSpareParts) {
    panne.installedSpareParts = [];
  }

  panne.installedSpareParts.push({
    sparepartId: "",
    taskId: "",
    panneId: panne.id!,
    quantity: 1,
  });
}

function removeInstalledSparePart(panne: Panne, index: number): void {
  if (!panne.installedSpareParts || index < 0 || index >= panne.installedSpareParts.length) {
    return;
  }

  panne.installedSpareParts.splice(index, 1);
}

function isSparePartAvailable(sparepartId: string): boolean {
  if (!sparepartId) {
    return false;
  }
  const sparePart = spareparts.value.find(
    (item) => item.id === sparepartId
  );
  return !!sparePart && sparePart.unitInStock > 0;
}

function getSparePartLabel(sparepartId: string): string {
  if (!sparepartId) {
    return "Pièce détachée";
  }
  return isSparePartAvailable(sparepartId)
    ? "Pièce détachée (Disponible en stock)"
    : "Pièce détachée (Indisponible en stock)";
}

async function onConfirm() {
  loading.value = true;
  try {
    await deletePanne(panne.value.id!);
    cancel();
    appStore.sendSuccessNotice("Panne has been deleted.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    removePanneDialog.value = false;
    loading.value = false;
  }
}

function onCancel() {
  removePanneDialog.value = false;
}

function isCreatingNewPanne(): boolean {
  return route.name === "newpanne" || route.params.id === "new";
}

onMounted(async () => {
  try {
    loading.value = true;
    const isNewPanne = isCreatingNewPanne();
    if (isNewPanne) {
      title.value = "Créer une panne";
      panne.value = {
        creationDate: new Date().toISOString().split("T")[0],
      } as Panne;
    } else {
      title.value = "Editer la panne";
      panne.value = await getPanneById(route.params.id as string);
      taskByPanne.value = await fetchTaskByPanne(route.params.id as string);
    }

    product.value = await getProductById(props.productId);
    if (isNewPanne) {
      panne.value.productAquaNumber = String(product.value.aquaNb ?? "");
    }
    spareparts.value = await fetchFilteredSpareParts([product.value.productMake], [product.value.productType]);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
});
</script>
